import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import {
  AddressPipe,
  BaseComponent,
  Claim,
  CsvParams,
  CsvService,
  FormatPipe,
  Payment,
  SearchResult,
  ToastType,
} from 'src/app/shared';
import { PaymentInfo, PaymentRequest } from 'src/app/shared/models/payment';
import { ClaimService } from '../../services/claim.service';
import { ConfigurationService } from '../../services/configuration.service';
import { InpageAliasService } from '../../services/inpage-alias.service';
import { Session } from '../../services/session.service';
import { UtilityService } from '../../services/utility.service';
import { LogService } from '../../services/log.service';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { TranslationPipe } from '../../pipes/translation.pipe';
import { TranslationService } from '../../services/translation.service';
import { Aliases } from '../../models/aliases';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-payment',
    providers: [AddressPipe, TranslationPipe, FormatPipe],
    template: `
    <app-loading-panel [state]="!paymentsLoaded ? 'Loading' : 'Loaded'">
      <ng-template #content>
        @if (showPaymentDate) {
          <div class="paymentMessage app-pb1">
            <app-toast type="Info"
              >{{'Your next payment will be sent on' | translation:translationRequest}}
              {{ nextPaymentDate | format:'date' }}
            </app-toast>
          </div>
        }
        @if (paymentsLoaded) {
          <app-list
            [dataSource]="isHomePage ? paymentsDataMyClaims : paymentData"
            [template]="row"
            class="app-pt2"
            [exportEnabled]="true"
            [exportButtonAriaLabel]="'Export and download these payments as a XLSX file'"
            [paginatorTemplate]="historyMessage"
            noDataIcon="monetization_on"
            [noDataMessage]="noDataMessage"
            (export)="export()"
            [tableAriaLabel]="'Payments'"
            >
            <ng-container header>
              <tr class="tw-flex tw-flex-wrap tw-w-full">
                <app-list-header
                  name="issueDate"
                  label="Issue Date"
                  class="tw-w-full md:tw-w-[15%]"
                ></app-list-header>
                <app-list-header
                  name="payee"
                  label="Payee"
                  class="tw-w-full md:tw-w-[21%]"
                ></app-list-header>
                <app-list-header
                  name="amount"
                  label="Amount"
                  class="tw-w-full md:tw-w-[15%]"
                  >
                </app-list-header>
                <app-list-header
                  name="paymentMethod"
                  label="Payment Method"
                  class="tw-w-full md:tw-w-[20%]"
                  >
                </app-list-header>
                <app-list-header
                  name="fromDate"
                  label="Detail"
                  class="tw-w-full md:tw-w-[28%]"
                ></app-list-header>
              </tr> </ng-container
            ></app-list>
          }
          <ng-template #row let-element>
            <tr class="tw-flex tw-flex-wrap tw-w-full tw-items-center">
              <td
                label="Issue Date"
                layout="row-xs"
                class="tw-w-full md:tw-w-[15%]"
                >
                <span value>
                  <app-value
                    class="inlineBlock helpIcon"
                    [value]="element.issueDate | format : 'date'"
                    [tabIndex]="0"
                    [translationNeeded]="false"
                  ></app-value>
                  <div
                    class="inlineBlock"
                    #tooltip="matTooltip"
                    [matTooltip]="element.issueDateHelpText | translation:translationRequest"
                    matTooltipPosition="below"
                    (mouseenter)="$event.stopImmediatePropagation()"
                    (mouseleave)="$event.stopImmediatePropagation()"
                    tabindex="0"
                    (click)="tooltip.toggle()"
                    (keydown.enter)="tooltip.toggle()"
                    >
                    <mat-icon
                      attr.alt="{{ 'Issue Date information' | translation:translationRequest }}"
                      class="helpIcon smallhelpicon"
                      >help_outline</mat-icon
                      >
                    </div>
                  </span>
                </td>
                    <td class="tw-w-full md:tw-w-[21%]">
                  <app-value label="payee" layout="row-xs" [translationNeeded]="false" [valueTemplate]="payeeTpl">
                    <ng-template #payeeTpl>
                      <span>
                        @if (element.payee && element.payee !== '') {
                          <span
                            class="hideIconMobile"
                            >
                            @if (showPayeeIcon(element)) {
                              <span
                                ><img
                                class="inlineBlock"
                                src="./assets/images/Employee.png"
                                width="25"
                                height="30"
                                alt=""
                              /></span>
                            }
                            @if (showMedicalIcon(element)) {
                              <span
                                ><img
                                class="inlineBlock"
                                src="./assets/images/Medical_Provider.png"
                                width="25"
                                height="30"
                                alt=""
                              /></span>
                            }
                          </span>
                        }
                        @if (!(session?.user && session?.user?.emulatorContext)) {
                          <span
                            class="text-left-margin"
                            tabindex="0"
                            >{{ element.payee }}</span
                            >
                          }
                          @if (session?.user && session?.user?.emulatorContext) {
                            <span
                              class="text-left-margin"
                              tabindex="0"
                              >{{ element.payee }}</span
                              >
                            }
                          </span>
                        </ng-template>
                      </app-value>
                    </td>
                    <td class="tw-w-full md:tw-w-[15%]">
                      <app-value label="amount" layout="row-xs" [tabIndex]="0" [translationNeeded]="false" [valueTemplate]="amountTpl">
                        <ng-template #amountTpl>
                          <span>
                            @if (!(session?.user && session?.user?.emulatorContext)) {
                              <span
                                >{{ element.amount | currency }}</span
                                >
                              }
                              @if (session?.user && session?.user?.emulatorContext) {
                                <span
                                  >{{ element.amount | currency }}</span
                                  >
                                }
                              </span>
                            </ng-template>
                          </app-value>
                        </td>
                        <td class="tw-w-full md:tw-w-[20%]">
                          <app-value
                            label="Payment Method"
                            layout="row-xs"
                            multilineHeaderStyle="pmt-header"
                            [valueTemplate]="paymentMethodTpl"
                            >
                            <ng-template #paymentMethodTpl>
                              <span>
                                <div class="app-pt1 app-pb1">
                                  <span tabindex="0">
                                    {{ getPaymentMethodText(element) | translation:translationRequest }}
                                  </span>
                                  @if (element.voided) {
                                    <span class="void" tabindex="0"
                                      >{{'VOID' | translation:translationRequest}}</span
                                      >
                                    }
                                    @if (element.documentType === 'Voucher') {
                                      <div
                                        tabindex="0"
                                        class="inlineBlock"
                                        #tooltipPaymentMethod="matTooltip"
                                        [matTooltip]="voucherHelpText | translation:translationRequest"
                                        matTooltipPosition="below"
                                        (mouseenter)="$event.stopImmediatePropagation()"
                                        (mouseleave)="$event.stopImmediatePropagation()"
                                        (click)="tooltipPaymentMethod.toggle()"
                                        (keydown.enter)="tooltipPaymentMethod.toggle()"
                                        >
                                        <mat-icon attr.alt="{{'Voucher information' | translation:translationRequest}}" class="helpIcon"
                                          >help_outline</mat-icon
                                          >
                                        </div>
                                      }
                                    </div>
                                  </span>
                                </ng-template>
                              </app-value>
                            </td>
                            <td class="tw-w-full md:tw-w-[28%]">
                              <app-value [valueTemplate]="detailsTpl">
                                <ng-template #detailsTpl>
                                  <span>
                                    @if (isHomePage) {
                                      <div>
                                        <strong class="label row-label detailstrong"
                                          >{{'CLAIM' | translation:translationRequest}}:
                                        </strong>
                                        <a
                                          [routerLink]="['/claims']"
                        [queryParams]="{
                          claimId: element.claimId,
                          source: element.source
                        }"
                                          >{{ element.claim | translation:translationRequest }}</a
                                          >
                                        </div>
                                      }
                                      @if (element.lob === 'DS') {
                                        <div tabindex="0">
                                          <strong class="label row-label">{{ 'GROSS-NET'  | translation:translationRequest}} :&nbsp;</strong>
                                          @if (!(session?.user && session?.user?.emulatorContext)) {
                                            <span
                                              >{{ element.billedAmount | currency }}</span
                                              >
                                            }
                                            @if (session?.user && session?.user?.emulatorContext) {
                                              <span
                                                >{{ element.billedAmount | currency }}</span
                                                >
                                              }
                                              -
                                              @if (!(session?.user && session?.user?.emulatorContext)) {
                                                <span
                                                  >{{ element.amount | currency }}</span
                                                  >
                                                }
                                                @if (session?.user && session?.user?.emulatorContext) {
                                                  <span
                                                    >{{ element.amount | currency }}</span
                                                    >
                                                  }
                                                  <div
                                                    class="inlineBlock"
                                                    #tooltip="matTooltip"
                                                    [matTooltip]="grossNetHelpText | translation:translationRequest"
                                                    matTooltipPosition="below"
                                                    (mouseenter)="$event.stopImmediatePropagation()"
                                                    (mouseleave)="$event.stopImmediatePropagation()"
                                                    (click)="tooltip.toggle()"
                                                    >
                                                    <mat-icon attr.alt="{{ 'Gross Net information' | translation:translationRequest }}" class="helpIcon"
                                                      >help_outline</mat-icon
                                                      >
                                                    </div>
                                                  </div>
                                                }
                                                @if (
                                                  !(session?.user && session?.user?.emulatorContext) &&
                                                  element.lob !== 'DS'
                                                  ) {
                                                  <div
                                                    tabindex="0"
                      [ngClass]="
                        !element.showAddress ? 'displayNone' : 'md:tw-block'
                      "
                                                    >
                                                    @if (element.showAddress) {
                                                      <strong
                                                        class="label row-label detailstrong"
                                                        >{{'MAILING ADDRESS' | translation:translationRequest}}:</strong
                                                        >
                                                        }<br />
                                                        <span
                        [innerHtml]="
                          addresssPipe.transform(element.mailingAddress)
                        "
                                                        ></span>
                                                      </div>
                                                    }
                                                  @if (
                                                    (!isHomePage && showFromToDate) ||
                                                    element.lob === 'DS' ||
                                                    element.lob === 'WC'
                                                    ) {
                                                    <div
                                                      tabindex="0"
                                                      class="fromTo"
                                                      >
                                                      <strong class="label row-label detailstrong"
                                                        >{{'FROM-TO' | translation:translationRequest}}:&nbsp;</strong
                                                        >
                                                        <app-value
                                                          [value]="element.fromDate | format : 'date'"
                                                          [translationNeeded]="false"
                                                          ></app-value
                                                          >&nbsp;-&nbsp;<app-value
                                                          style="display:inline"
                                                          [value]="element.toDate | format : 'date'"
                                                          [translationNeeded]="false"
                                                        ></app-value>
                                                      </div>
                                                    }
                                                    @if (
                                                      element.icn !== undefined &&
                                                      element.vendorId !== undefined &&
                                                      this.isViewingOwnPmt
                                                      ) {
                                                      <div
                                                        >
                                                        <span>
                                                          @if (
                                                            !(session?.user && session?.user?.emulatorContext)
                                                            ) {
                                                            <a
                                                              class="link"
                                                              (click)="downloadFile(element)"
                                                              [routerLink]=""
                                                              target="_blank"
                                                              >{{'Explanation of Bill Review' | translation:translationRequest}}</a
                                                              >
                                                            }
                                                            @if (session?.user && session?.user?.emulatorContext) {
                                                              <span
                                                                >{{ 'Explanation of Bill Review' | translation:translationRequest }}</span
                                                                >
                                                            }
                                                          </span>
                                                        </div>
                                                      }
                                                    </span>
                                                  </ng-template>
                                                </app-value>
                                              </td>
                                                </tr>
                                              </ng-template>
                                              <ng-template #historyMessage>
                                                @if (!isHomePage) {
                                                  <i>{{ 'Payment history for past 12 months.' | translation:translationRequest}}</i>
                                                }
                                                @if (isHomePage) {
                                                  <i
                                                    >{{ 'Payment history on open claims for the past 12 months.' | translation:translationRequest}}</i
                                                    >
                                                  }
                                                  <div
                                                    class="inlineBlock"
                                                    #tooltip="matTooltip"
                                                    [matTooltip]="paymentHistoryHelpText | translation:translationRequest"
                                                    matTooltipPosition="below"
                                                    (mouseenter)="$event.stopImmediatePropagation()"
                                                    (mouseleave)="$event.stopImmediatePropagation()"
                                                    (click)="tooltip.toggle()"
                                                    >
                                                    <mat-icon
                                                      attr.alt="{{ 'Payment History information' | translation:translationRequest }}"
                                                      class="helpIcon smallhelpicon"
                                                      >help_outline</mat-icon
                                                      >
                                                    </div>
                                                  </ng-template>
                                                  <ng-template #payment let-payment>
                                                    <div class="app-pt1 app-pb1">
                                                      {{ payment.paymentMethod }}
                                                      @if (payment.voided) {
                                                        <span class="void">{{ 'VOID' | translation:translationRequest }}</span>
                                                      }
                                                    </div>
                                                    @if (payment.paymentMethod === 'Check') {
                                                      <div class="app-pb1">
                                                        <span>addresssPipe.transform(payment.mailingAddress)</span>
                                                      </div>
                                                    }
                                                  </ng-template>
                                                </ng-template>
                                              </app-loading-panel>
    `,
    styles: [
        `
      .address {
        font-size: 12px;
      }

      .paymentMessage {
        max-width: 400px;
      }

      .void {
        background-color: var(--sed-sedgwick-green-color);
        color: var(--sed-dynamic-black-color);
        font-weight: bold;
        padding: 4px;
        border-radius: 4px;
        margin-left: 4px;
      }

      .displayNone {
        display: none;
      }
      #explanation::ng-deep.row-xs-label:after {
        content: '';
      }

      .fromTo > *,
      .inlineBlock {
        display: inline-block !important;
      }

      .helpIcon {
        vertical-align: middle;
      }

      @media (max-width: 800px) {
        .hideIconMobile {
          display: none;
          visibility: hidden;
        }
      }
      .detailstrong {
        font-size: 11px !important;
      }
      .smallhelpicon {
        font-size: 18px !important;
        height: 18px;
        width: 18px;
        margin-left: 4px;
      }
      .text-left-margin {
        margin-left: 4px;
      }
    `,
    ],
    standalone: false
})
export class PaymentComponent extends BaseComponent implements OnChanges {
  voucherHelpText: string =
    'Voucher is the calculated benefit amount Sedgwick provided to the payer of your benefits. The amount listed is the gross amount and does not include taxes or deductions.';
  //"Voucher is an advised amount to pay provided to your employer by Sedgwick. The amount listed is the gross amount before deductions and a net amount will be provided through your employer's payroll process.";
  issueDateCheckHelpText: string = 'Date when the payment was mailed.';
  issueDateDirectDepositHelpText: string =
    'Date when the payment was deposited.';
  issueDateVoucherHelpText: string =
    "Date when an advised amount to pay was sent to your employer to be issued through your employer's payroll process.";
  paymentHistoryFirst: string =
    'Requests for additional details or dates may be sent to your ';
  paymentHistoryLast: string = " through mySedgwick's Communication Center.";
  examinerTemplate: string = '%examiner%';
  paymentHistoryHelpText: string = '';
  myClaimsNoData: string =
    '<br><strong>No payments have been issued for your open claim(s)</strong>';
  claimSummaryNoData: string =
    '<br><strong>No payments have been issued for this claim</strong>';
  noDataMessage: string = '';
  grossNetHelpText: string =
    'Gross is the payment amount before deductions. Net is the payment amount after deductions.';

  @Input()
  payments: Payment[];
  @Input()
  paymentsLoaded: boolean = false;

  @Input()
  claim: SearchResult;
  clientMessage: string;

  @Input()
  claims: SearchResult[];

  @Input()
  showTitle = true;
  @Input()
  paymentData: Payment[];

  @Input()
  allPaymentData: Payment[] = [];

  @Input()
  isHomePage = false;

  localPaymentsData: Payment[] = [];

  @Input()
  paymentsDataMyClaims: any[];

  @Input()
  paymentMyClaims: any[];

  @Input()
  translationRequest:TranslationRequest
  
  isClaimant: boolean = false;

  paymentInfo: PaymentInfo;

  nextPaymentDate: Date;
  showPaymentDate: boolean;
  showFromToDate: boolean;
  toastMsg: string;
  toastIcon: string;
  toastType: ToastType;
  displayToastNoDataMsg: boolean = false;
  isPayments: boolean;
  isDisplayDetailsColumn: boolean = false;
  isEmulating: boolean = false;
  isLoaded: boolean = false;

  payeeIconLobList: string[] = ['DS', 'GL', 'AU', 'PR'];

  isViewingOwnPmt: boolean = false;

  constructor(
    public claimConfigService: ConfigurationService,
    public claimService: ClaimService,
    public addresssPipe: AddressPipe,
    private utilityService: UtilityService,
    private csvService: CsvService,
    private datePipe: DatePipe,
    public session: Session,
    private configurationService: ConfigurationService,
    public inpageAliasService: InpageAliasService,
    public logService: LogService,
    public translationPipe: TranslationPipe,
    private translationService: TranslationService,
    private formatPipe: FormatPipe
  ) {
    super();

    this.isEmulating =
      this.session.user &&
      this.session.user?.emulatorContext &&
      this.session.user?.emulatorContext?.isEmulating;
  }

  async ngOnInit(): Promise<void> {
    if (this.isHomePage) {
      this.isViewingOwnPmt = this.paymentMyClaims.some(
        (claim) => claim.userOwns === true
      );
    } else {
      this.isViewingOwnPmt = this.claim.userOwns;
    }
    this.localPaymentsData = this.paymentsDataMyClaims;
    this.isClaimant =
      this.session.user.role.toUpperCase() === 'HRM' &&
      this.session.user.authenticationType === 'CLM';
    this.noDataMessage = this.isHomePage
      ? this.myClaimsNoData
      : this.claimSummaryNoData;
    this.paymentData = [];
    this.session.user ? await this.session.loadUser() : null;
    if (
      this.claim?.listClientMessage &&
      this.claim?.listClientMessage.length > 0
    ) {
      this.clientMessage = this.claim?.listClientMessage.find(
        (x) => x.keyTab == 'Payments'
      )?.message;
    }
    if (!this.isHomePage && (this.claim != null || this.claim != undefined)) {
      this.paymentInfo = await this.claimService.getPayments(
        this.buildPaymentRequest(this.claim)
      );
      this.paymentData = this.paymentInfo.payments;
      this.nextPaymentDate = this.paymentInfo.nextPaymentDue;
      this.showPaymentDate = this.nextPaymentDate != null;
      this.showFromToDate = this.paymentInfo.showFromToDate;
      this.isPayments =
        this.paymentInfo.payments && this.paymentInfo.payments.length > 0;
      if (this.isPayments) {
        this.isDisplayDetailsColumn =
          this.paymentInfo.payments.find((x) => x.icn && x.vendorId) != null &&
          this.paymentInfo.payments.find((x) => x.icn && x.vendorId) !=
            undefined
            ? true
            : false;
      }
      if (this.showPaymentDate) {
        this.displayToastNoDataMsg = true;
        this.toastMsg =
          this.nextPaymentDate != null
            ? 'Your next payment will be sent on ' +
              this.formatPipe.transform(this.nextPaymentDate, 'date')
            : 'Your next payment will be sent on ';
        this.toastIcon = 'info';
        this.toastType = 'Info';
      }
    }

    const lobConfigs = await this.configurationService.getFeatureLobs(
      'payments'
    );
    if (this.isHomePage) {
      const userClaims = this.paymentMyClaims;
      const allPaymentDataTemp = <any>[];
      let paymentInfoTemp;
      if (this.localPaymentsData) {
        for (let i = 0; i < this.localPaymentsData.length; i++) {
          const claim = userClaims.find(
            (x) => x.claimId === this.localPaymentsData[i].claimId
          );
          if (
            !claim ||
            claim.status === 'Closed' ||
            !lobConfigs.some(
              (x) => x.value === claim.lineOfBusiness && x.key === 'payments'
            )
          ) {
            continue;
          } else {
            paymentInfoTemp = this.localPaymentsData[i];
            paymentInfoTemp.claim =
              claim.claimDescription + ' - ' + claim.claimNumber;
            paymentInfoTemp.claimId = claim.claimId;
            paymentInfoTemp.source = claim.source;
            paymentInfoTemp.lob = claim.dataSet;
            allPaymentDataTemp.push(paymentInfoTemp);
          }
        }
        this.paymentsDataMyClaims = allPaymentDataTemp;
        this.isPayments = this.paymentsDataMyClaims.length > 0;
        this.showFromToDate = true;
        if (this.isPayments) {
          this.isDisplayDetailsColumn =
            this.paymentsDataMyClaims.find((x) => x.icn && x.vendorId) !=
              null &&
            this.paymentsDataMyClaims.find((x) => x.icn && x.vendorId) !=
              undefined
              ? true
              : false;
        }
      }
    }

    if (this.paymentData) {
      this.setIssueDateHelpText(this.paymentData);
      //this.isLoaded = true;
      this.paymentsLoaded = true;
    }

    if (this.paymentsDataMyClaims) {
      this.setIssueDateHelpText(this.paymentsDataMyClaims);
      this.paymentsLoaded = true;
    }

    const examinerAlias: Aliases = await this.inpageAliasService.replaceAliases(
      'examinerSingular',
      this.configurationService.GetConfig.configFeatures.aliases
    );
    this.paymentHistoryHelpText = this.inpageAliasService.composeSentence([
      'Requests for additional details or dates may be sent to your',
      { alias: examinerAlias },
      "through mySedgwick's Communication Center.",
    ]);
  }

  buildPaymentRequest(claim: SearchResult): PaymentRequest {
    const userClaim = claim.userOwns ?? false;
    return new PaymentRequest(
      [
        {
          lob: claim.lob,
          claimId: claim.claimId,
          source: claim.source,
          userOwns: userClaim,
          claimNumber: claim.claimNumber,
        },
      ],
      userClaim
    );
  }
  buildPaymentRequestFromClaim(claim: Claim): PaymentRequest {
    const userClaim = claim.userOwns ?? false;
    return new PaymentRequest(
      [
        {
          lob: claim.dataSet,
          claimId: claim.claimId,
          source: claim.source,
          userOwns: userClaim,
          claimNumber: claim.claimNumber,
        },
      ],
      userClaim
    );
  }

  addToArray(
    key: string,
    header: string,
    csvParams: CsvParams //, keys: string[],  headers: string[]
  ) {
    csvParams.keys.push(key);
    csvParams.headers.push(header);
  }

  showPayeeIcon(payment: Payment) {
    //WC
    if (payment.lob?.toUpperCase() === 'WC' && payment.nameType === 'C') {
      return true;
    }
    //DS, GL, AU, PR
    if (
      this.payeeIconLobList.indexOf(payment.lob?.toUpperCase()) > -1 &&
      payment.nameType === 'C'
    ) {
      return true;
    }

    return false;
  }

  showMedicalIcon(payment: Payment) {
    // WC
    if (payment.lob?.toUpperCase() === 'WC' && payment.nameType === 'P') {
      return true;
    }
    //DS, GL, AU, PR
    if (
      this.payeeIconLobList.indexOf(payment.lob?.toUpperCase()) > -1 &&
      payment.nameType === 'P'
    ) {
      return true;
    }

    return false;
  }

  async generateExportColumns(csvParams: CsvParams) {
    // Translate all headers asynchronously
    const translationPromises = [
      { key: 'issueDate', label: 'Issue Date' },
      { key: 'paymentMethod', label: 'Payment Method' },
      { key: 'voided', label: 'Payment Voided' },
      ...(!this.isEmulating ? [
        { key: 'mailingAddress', label: 'Mailing Address' },
        { key: 'city', label: 'Mailing City' },
        { key: 'state', label: 'Mailing State' },
        { key: 'zipcode', label: 'Mailing ZipCode' },
        { key: 'payee', label: 'Payee' },
        { key: 'amount', label: 'Amount' },
      ] : []),
      { key: 'fromDate', label: 'From Date' },
      { key: 'toDate', label: 'To Date' },
    ].map(async ({ key, label }) => {
      const translatedLabel = await firstValueFrom(
        this.translationService.getTranslationForKey$(label, this.translationRequest)
      );
      this.addToArray(key, translatedLabel, csvParams);
    });

    await Promise.all(translationPromises);
    return csvParams;
  }

  async export() {
    const csvParams = new CsvParams();
    csvParams.fileName = 'Payments';
    csvParams.rows = this.paymentData;

    const tempPaymentData: any[] = [];
    const actualData = this.isHomePage
      ? this.paymentsDataMyClaims
      : this.paymentData;

    for (let i = 0; i < actualData.length; i++) {
      let combinedStreetAddress = actualData[i].mailingAddress.street1;
      combinedStreetAddress += actualData[i].mailingAddress.street2
        ? ' ' + actualData[i].mailingAddress.street2
        : '';
      const isVoucher = actualData[i].documentType === 'Voucher';
      tempPaymentData.push({
        issueDate: this.formatPipe.transform(actualData[i].issueDate, 'date'),
        paymentMethod: this.translationPipe.transform(isVoucher ? 'Voucher' : actualData[i].paymentMethod, this.translationRequest ),
        voided: this.translationPipe.transform(actualData[i].voided.toString(), this.translationRequest),
        fromDate: this.formatPipe.transform(actualData[i].fromDate, 'date'),
        toDate: this.formatPipe.transform(actualData[i].toDate, 'date'),
        mailingAddress:
          this.translationPipe.transform(isVoucher || !actualData[i].showAddress || actualData[i].lob === 'DS'
            ? ''
            : combinedStreetAddress, this.translationRequest),
        city:
        this.translationPipe.transform(isVoucher || !actualData[i].showAddress || actualData[i].lob === 'DS'
            ? ''
            : actualData[i].mailingAddress.city, this.translationRequest),
        state:
        this.translationPipe.transform(isVoucher || !actualData[i].showAddress || actualData[i].lob === 'DS'
            ? ''
            : actualData[i].mailingAddress.state, this.translationRequest),
        zipcode:
          isVoucher || !actualData[i].showAddress || actualData[i].lob === 'DS'
            ? ''
            : actualData[i].mailingAddress.zipCode,
        payee: this.translationPipe.transform(actualData[i].payee, this.translationRequest),
        amount: actualData[i].amount,
      });
    }

    csvParams.rows = tempPaymentData;
    csvParams.keys = [];
    csvParams.headers = [];

    await this.generateExportColumns(csvParams); // Make this await

    this.csvService.download(csvParams);
  }
  async downloadFile(payment: Payment) {
    try {
      this.claimService
        .downloadPaymentDocument(
          this.isHomePage ? payment.source : this.claim.source,
          this.isHomePage ? payment.claimId : this.claim.claimId,
          payment.icn,
          payment.vendorId
        )
        .then((resp: any) => {
          if (resp && resp?.content) {
            /* -- Remove code */
            //const featureName: string = 'ExplanationOfBillReview';
            //let linkSection = 'Payments';
            //linkSection = 'Explantion of bill review';
            /*             this.logService.LogFeature(
              this.claim,
              featureName,
              linkSection,
              payment.icn
            ); */
            const filename = resp.content.headers[0].value[0];

            const contentType = resp.content.headers[1].value[0];
            const arrayBuffer = resp.content.headers[2].value[0];
            if (filename && contentType && arrayBuffer) {
              const binaryString = window.atob(arrayBuffer);
              const binaryLen = binaryString.length;
              const bytes = new Uint8Array(binaryLen);
              for (let i = 0; i < binaryLen; i++) {
                const ascii = binaryString.charCodeAt(i);
                bytes[i] = ascii;
              }
              if (bytes) {
                const blob = new Blob([bytes], { type: contentType });
                this.utilityService.downloadFile(blob, filename);
              }
            }
          }
        });
    } catch (error) {
      console.dir(error);
    }
  }

  setIssueDateHelpText(payments: Payment[]) {
    for (let i = 0; i < payments.length; i++) {
      if (payments[i].documentType === 'Voucher') {
        payments[i].issueDateHelpText = this.issueDateVoucherHelpText;
      } else if (payments[i].paymentMethod == 'Check') {
        payments[i].issueDateHelpText = this.issueDateCheckHelpText;
      } else if (payments[i].paymentMethod == 'Direct Deposit') {
        payments[i].issueDateHelpText = this.issueDateDirectDepositHelpText;
      }
    }
  }

  getPaymentMethodText(element: Payment) {
    switch (element.documentType) {
      case 'Voucher':
        return 'Voucher';
    }
    return element.paymentMethod;
  }

  async ngOnChanges(changes: any) {
    if (changes.paymentsDataMyClaims?.currentValue && this.isHomePage) {
      const lobConfigs = await this.configurationService.getFeatureLobs(
        'payments'
      );
      const userClaims = this.paymentMyClaims;
      const allPaymentDataTemp = <any>[];
      let paymentInfoTemp;
      if (this.paymentsDataMyClaims) {
        for (let i = 0; i < this.paymentsDataMyClaims.length; i++) {
          const claim = userClaims.find(
            (x) => x.claimId === this.paymentsDataMyClaims[i].claimId
          );
          if (
            !claim ||
            claim.status === 'Closed' ||
            lobConfigs.filter((x) => x.value === claim.dataSet)?.length === 0
          ) {
            continue;
          } else {
            paymentInfoTemp = this.paymentsDataMyClaims[i];
            paymentInfoTemp.claim =
              claim.claimDescription + ' - ' + claim.claimNumber;
            paymentInfoTemp.claimId = claim.claimId;
            paymentInfoTemp.source = claim.source;
            paymentInfoTemp.lob = claim.dataSet;
            allPaymentDataTemp.push(paymentInfoTemp);
          }
        }
      }
      this.paymentsDataMyClaims = allPaymentDataTemp;
      this.isPayments = this.paymentsDataMyClaims.length > 0;
      this.showFromToDate = true;
      if (this.isPayments) {
        this.isDisplayDetailsColumn =
          this.paymentsDataMyClaims.find((x) => x.icn && x.vendorId) != null &&
          this.paymentsDataMyClaims.find((x) => x.icn && x.vendorId) !=
            undefined
            ? true
            : false;
      }
    }
    if (this.paymentData) {
      this.setIssueDateHelpText(this.paymentData);
    }
    if (this.paymentsDataMyClaims) {
      this.setIssueDateHelpText(this.paymentsDataMyClaims);
    }
  }
}
